import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Mail, MapPin, Phone, Linkedin, ArrowRight, Clock } from 'lucide-react';
import ContactForm from '../components/forms/ContactForm';
import ScheduleMeetingForm from '../components/forms/ScheduleMeetingForm';

const EMAIL = 'support@zendatasolutions.net';

const Contact = () => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const contactInfo = [
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email',
      value: EMAIL,
      link: `mailto:${EMAIL}`,
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Office',
      value: 'Toronto, Canada',
      link: null,
    },
    {
      icon: <Linkedin className="w-6 h-6" />,
      title: 'LinkedIn',
      value: 'Connect with us',
      link: 'https://linkedin.com/company/zendatasolutions',
    },
  ];

  const faqs = [
    {
      question: 'How long does a typical project take?',
      answer: 'Project timelines vary based on scope and complexity. Our rapid prototype approach delivers results in 7-90 days, while comprehensive digital transformations may span 6-12 months.',
    },
    {
      question: 'Do you work with startups?',
      answer: 'Yes! We work with companies of all sizes, from innovative startups to Fortune 500 enterprises. Our solutions are scalable and can grow with your business.',
    },
    {
      question: 'What industries do you serve?',
      answer: 'We have experience across multiple industries including healthcare, finance, retail, manufacturing, and technology. Our solutions are adaptable to various business contexts.',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 grain-texture" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-medium tracking-widest uppercase text-blue-600 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Contact Us
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.95] text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Let's Start a Conversation
            </h1>
            <p className="text-lg md:text-xl text-slate-500 leading-relaxed">
              Have a question or want to discuss a project? We'd love to hear from you. Reach out and we'll get back to you within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-16 md:py-24" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h2 className="text-2xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Send us a message
              </h2>
              <ContactForm />
            </div>

            {/* Info */}
            <div>
              <h2 className="text-2xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Get in touch
              </h2>
              <p className="text-slate-500 mb-8 leading-relaxed">
                Prefer to schedule a call? Use the button below to book a time that works for you, or view our calendar directly.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-12">
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-6 text-base btn-click"
                      data-testid="contact-schedule-btn"
                    >
                      Schedule a meeting
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                        Schedule a Meeting
                      </DialogTitle>
                      <DialogDescription>
                        Fill out the form and we'll get back to you within 24 hours.
                      </DialogDescription>
                    </DialogHeader>
                    <ScheduleMeetingForm onSuccess={() => setDialogOpen(false)} />
                  </DialogContent>
                </Dialog>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {contactInfo.map((info, idx) => (
                  <div key={idx} className="flex items-start gap-4" data-testid={`contact-info-${idx}`}>
                    <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 flex-shrink-0">
                      {info.icon}
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">{info.title}</p>
                      {info.link ? (
                        <a 
                          href={info.link}
                          target={info.link.startsWith('http') ? '_blank' : undefined}
                          rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="text-slate-900 font-medium hover:text-blue-600 transition-colors"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-slate-900 font-medium">{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Office Hours */}
              <div className="mt-12 p-6 bg-slate-50 rounded-xl" data-testid="office-hours">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-5 h-5 text-slate-600" />
                  <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                    Office Hours (EST)
                  </h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Monday - Friday</span>
                    <span className="text-slate-900">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Saturday - Sunday</span>
                    <span className="text-slate-900">Closed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="faq-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-sm font-medium tracking-widest uppercase text-slate-500 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                FAQ
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                Frequently Asked Questions
              </h2>
            </div>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <div 
                  key={idx}
                  className="p-6 bg-white rounded-xl border border-slate-100"
                  data-testid={`faq-${idx}`}
                >
                  <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                    {faq.question}
                  </h3>
                  <p className="text-slate-500 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="h-96 bg-slate-200" data-testid="map-section">
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-500">Toronto, Canada</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
